// The simulated data source for the app while the API is built. Owns the
// world (every league Ken is in), a controllable clock (play, pause, speed,
// jump), and the few mutations the screens need (lineup swaps). Everything
// the screens read comes through the hooks in ./hooks.ts, which take the
// same view shapes the API will serve, so swapping this for the real backend
// is a data-layer job.
//
// The active league comes from the route (`/l/:leagueId/...`, SITE_HOME.md).
// Hub-level screens have no active league and see the world instead.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useSession } from './session';
import { leagueIdFromPath } from './route';
import {
  StoredLeagueSettings,
  type LeagueSettings,
  type StickerId,
  type WeeklyHat,
  type WizardAnswers,
} from '@fsp/domain';
import type { DraftEvent, DraftIntent } from '@fsp/draft';
import {
  advanceLeague,
  createLeagueFromWizard,
  postChat as simPostChat,
  reactChat as simReactChat,
  draftIntent as simDraftIntent,
  startDraft as simStartDraft,
  placeClaim as simPlaceClaim,
  withdrawClaim as simWithdrawClaim,
  reorderClaims as simReorderClaims,
  addPlayer as simAddPlayer,
  dropPlayer as simDropPlayer,
  undoTransaction as simUndoTransaction,
  tradeIntent as simTradeIntent,
  setTradeBlock as simSetTradeBlock,
  createWorld,
  defaultNow,
  editTeam as simEditTeam,
  pickHat as simPickHat,
  joinLeague as joinSimLeague,
  previewSettings as previewSimSettings,
  saveSettings as saveSimSettings,
  slugify,
  type ClaimInput,
  type SaveResult,
  type SettingsPreview,
  type SimState,
} from '@fsp/sim';
import type { Transaction } from '@fsp/waivers';
import type { TradeEvent, TradeIntent } from '@fsp/trades';
import { football } from '@fsp/sports-football';
import { storageKey } from './storage';
import { useChapterOpen } from './tutorialChapter';

export type SimSpeed = 1 | 30 | 300;

/** Which week the simulated world is in: the regular season (Week 3), the playoffs (Week 16, semifinals), or the championship (Week 17, the final). */
export type SimScenario = 'regular' | 'playoffs' | 'championship';
const SCENARIO_PERIOD: Record<SimScenario, number> = { regular: 3, playoffs: 16, championship: 17 };
const SCENARIO_KEY = storageKey('sim.scenario.v1');
const SEED_KEY = storageKey('sim.seed.v1');
/** "New random league" survives a reload: the seed lives with the clock and the scenario. */
function readSeed(fallback: number): number {
  try {
    const v = Number(sessionStorage.getItem(SEED_KEY));
    return Number.isFinite(v) && v > 0 ? v : fallback;
  } catch {
    return fallback;
  }
}
function writeSeed(seed: number): void {
  try {
    sessionStorage.setItem(SEED_KEY, String(seed));
  } catch {
    // Storage blocked: the seed lasts for this page load.
  }
}
function readScenario(): SimScenario {
  try {
    const v = sessionStorage.getItem(SCENARIO_KEY);
    return v === 'playoffs' || v === 'championship' ? v : 'regular';
  } catch {
    return 'regular';
  }
}

export type JumpPreset =
  'thu-pre' | 'sun-pre' | 'sun-mid' | 'sun-late' | 'sun-night' | 'final' | 'wed-waivers';

export const JUMP_PRESETS: { id: JumpPreset; label: string; hint: string }[] = [
  {
    id: 'thu-pre',
    label: 'Thursday, before kickoff',
    hint: 'Everything unlocked, all projections',
  },
  { id: 'sun-pre', label: 'Sunday 12:55', hint: 'Thursday final, early games about to start' },
  { id: 'sun-mid', label: 'Sunday 3:30', hint: 'Early games in Q4, late games not started' },
  { id: 'sun-late', label: 'Sunday 6:30', hint: 'Late games live' },
  { id: 'sun-night', label: 'Sunday night', hint: 'Night game live, family draft room open' },
  { id: 'final', label: 'Tuesday', hint: 'Week final' },
  { id: 'wed-waivers', label: 'Wednesday 3:05 AM', hint: 'Waivers processed, results in the log' },
];

export interface SimContextValue {
  /** Every league Ken is in. */
  world: SimState[];
  /** The league the current route is inside; null on hub-level screens. */
  activeLeagueId: string | null;
  /** The active league, or the first league when there is none, so league code always has one. */
  state: SimState;
  /** Bumped on every mutation so memoized views recompute. */
  version: number;
  now: number;
  playing: boolean;
  speed: SimSpeed;
  setPlaying: (on: boolean) => void;
  setSpeed: (s: SimSpeed) => void;
  setNow: (ms: number) => void;
  jump: (preset: JumpPreset) => void;
  reseed: (seed: number) => void;
  scenario: SimScenario;
  /** Rebuild the world at another point in the season; the journal is cleared. */
  setScenario: (s: SimScenario) => void;
  /**
   * Tutorial mode: the world is the seed alone, so a chapter reads the same
   * every time and nothing done inside it is recorded. Turning it on or off
   * rebuilds the world.
   */
  tutorial: boolean;
  setTutorial: (on: boolean) => void;
  /**
   * Swap the player in a lineup row of the active league with a roster
   * player (or empty the row). Returns a reason string when refused.
   */
  swap: (teamId: string, slot: string, slotIndex: number, playerId: string | null) => string | null;
  /** The same swap addressed to any league; the hub's chores use it. */
  swapIn: (
    leagueId: string,
    teamId: string,
    slot: string,
    slotIndex: number,
    playerId: string | null,
  ) => string | null;
  /** The setup wizard's last step (LEAGUE_SETUP.md). Returns the new league's id. */
  createLeague: (answers: WizardAnswers, ownerName: string) => string;
  /** A league by invite code, joined or not; null when the code is unknown. */
  leagueByCode: (code: string) => SimState | null;
  /** Take a seat in a league found by code. Returns the league id, or a reason it failed. */
  joinLeague: (
    code: string,
    member: { ownerName: string; teamName: string; pro: string },
  ) => { ok: true; leagueId: string } | { ok: false; reason: string };
  /** Save a settings version on the active league (LEAGUE_SETTINGS.md). */
  saveSettings: (
    settings: LeagueSettings,
    opts: { reason: string; by: string; unlock?: boolean },
  ) => SaveResult;
  /** Score the current period under candidate settings, same engine. */
  previewSettings: (settings: LeagueSettings) => SettingsPreview;
  /** A draft-room intent on the active league (pick, queue, commissioner controls). */
  draftIntent: (intent: DraftIntent) => DraftEvent[];
  /** Commissioner starts the draft now. */
  draftStart: () => DraftEvent[];
  /** Post to the active league's channel (CHAT.md). */
  postChat: (
    author: string,
    body: string,
    color: string | null,
    replyTo?: string | null,
    /** A sticker, drawn with my team (PLAN-2026-10-30-stickers); `body` is then unused. */
    sticker?: StickerId,
  ) => void;
  /** Toggle my reaction on a message. */
  reactChat: (messageId: string, emoji: string) => void;
  /** Place or edit a waiver claim on the active league (WAIVERS.md). */
  placeClaim: (input: Omit<ClaimInput, 'teamId'>) => { ok: true; claimId: string } | MoveRefusal;
  withdrawClaim: (claimId: string) => string | null;
  reorderClaims: (claimIds: string[]) => string | null;
  /** Add a free agent, dropping as needed. Returns the transaction for the undo toast. */
  addPlayer: (
    playerId: string,
    dropPlayerIds: string[],
  ) => { ok: true; transaction: Transaction } | MoveRefusal;
  dropPlayer: (playerId: string) => { ok: true; transaction: Transaction } | MoveRefusal;
  /** Reverse an add or drop within its five-minute window. */
  undoTransaction: (transactionId: string) => string | null;
  /** A trade intent on the active league (TRADES.md): propose, counter, accept, decline, withdraw, vote, approve, veto. */
  tradeIntent: (intent: TradeIntent) => TradeEvent[];
  /** Put one of my players on the trade block with what I want back, or take him off (null). */
  setTradeBlock: (playerId: string, wants: string | null) => string | null;
  /** Rename or recolor my team in the active league (SCENARIOS L2). Returns a reason when refused. */
  editTeam: (change: TeamChange) => string | null;
  /** My team puts the week's hat on its low scorer; false when it may not. */
  pickHat: (hat: WeeklyHat) => boolean;
}

/** What the Edit team sheet sends: a name, colors, or both (`UpdateTeamRequest`). */
export type TeamChange = { name?: string; colors?: { primary: string; secondary: string } };

export type MoveRefusal = { ok: false; reason: string };

const SimContext = createContext<SimContextValue | null>(null);

const CLOCK_KEY = storageKey('sim.clock.v1');

function readClock(): { now: number; playing: boolean; speed: SimSpeed } | null {
  try {
    const raw = sessionStorage.getItem(CLOCK_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as { now?: unknown; playing?: unknown; speed?: unknown };
    if (typeof v.now !== 'number' || typeof v.playing !== 'boolean') return null;
    const speed = v.speed === 1 || v.speed === 30 || v.speed === 300 ? v.speed : 30;
    return { now: v.now, playing: v.playing, speed };
  } catch {
    return null;
  }
}

// Leagues created or joined in this browser session survive a reload: the
// journal replays the wizard answers and joins onto a fresh world.
const JOURNAL_KEY = storageKey('sim.journal.v1');
interface Journal {
  created: { id: string; answers: WizardAnswers; ownerName: string }[];
  joined: { code: string; member: { ownerName: string; teamName: string; pro: string } }[];
  settings: {
    leagueId: string;
    settings: LeagueSettings;
    reason: string;
    by: string;
    unlock: boolean;
    now: number;
  }[];
  /** Draft room actions in order, replayed against the clock so a reload keeps the draft. */
  draft: DraftEntry[];
  /** Roster moves and claims, replayed in time order alongside the draft entries. */
  moves: MoveEntry[];
}
type DraftEntry =
  | { leagueId: string; at: number; kind: 'start' }
  | { leagueId: string; at: number; kind: 'intent'; intent: DraftIntent }
  | { leagueId: string; at: number; kind: 'react'; messageId: string; emoji: string; who: string }
  | {
      leagueId: string;
      at: number;
      kind: 'chat';
      author: string;
      body: string;
      color: string | null;
      /** The line it answers (C2); absent in a journal written before. */
      replyTo?: string | null;
      /** A sticker (PLAN-2026-10-30-stickers); absent for words. */
      sticker?: StickerId;
    };
type MoveEntry =
  | { leagueId: string; at: number; kind: 'claim'; input: ClaimInput }
  | { leagueId: string; at: number; kind: 'withdraw'; claimId: string }
  | { leagueId: string; at: number; kind: 'reorder'; teamId: string; claimIds: string[] }
  | {
      leagueId: string;
      at: number;
      kind: 'add';
      teamId: string;
      playerId: string;
      dropPlayerIds: string[];
    }
  | { leagueId: string; at: number; kind: 'drop'; teamId: string; playerId: string }
  | { leagueId: string; at: number; kind: 'undo'; transactionId: string }
  | { leagueId: string; at: number; kind: 'trade'; intent: TradeIntent }
  | {
      leagueId: string;
      at: number;
      kind: 'block';
      teamId: string;
      playerId: string;
      wants: string | null;
    }
  | { leagueId: string; at: number; kind: 'team'; teamId: string; change: TeamChange }
  /** The week's hat put on (PLAN-2026-10-30-the-weekly-hat). */
  | { leagueId: string; at: number; kind: 'hat'; teamId: string; hat: WeeklyHat };
const EMPTY_JOURNAL: Journal = { created: [], joined: [], settings: [], draft: [], moves: [] };

/**
 * Tutorial mode (DEMO.md). Each chapter stands alone and is identical on
 * every visit.
 *
 * The world is `createWorld(seed)` plus a replayed journal, so a chapter is
 * identical every time exactly when the journal is left out of it. In tutorial
 * mode the journal is neither read nor written: what the visitor does lives in
 * the in-memory world and is gone the next time the chapter is entered.
 *
 * That also means the tutorial can never touch a signed-in manager's own
 * leagues, which is why this is a filter on the journal rather than a reset of
 * it.
 */
const TUTORIAL_KEY = storageKey('tutorial.v1');

export function isTutorial(): boolean {
  try {
    return sessionStorage.getItem(TUTORIAL_KEY) === '1';
  } catch {
    return false;
  }
}

function writeTutorialFlag(on: boolean): void {
  try {
    if (on) sessionStorage.setItem(TUTORIAL_KEY, '1');
    else sessionStorage.removeItem(TUTORIAL_KEY);
  } catch {
    // Storage blocked: the tutorial lasts for this page load.
  }
}

function readJournal(): Journal {
  if (isTutorial()) return { ...EMPTY_JOURNAL };
  try {
    const raw = sessionStorage.getItem(JOURNAL_KEY);
    if (!raw) return { ...EMPTY_JOURNAL };
    const v = JSON.parse(raw) as Partial<Journal>;
    return {
      created: v.created ?? [],
      joined: v.joined ?? [],
      settings: v.settings ?? [],
      draft: v.draft ?? [],
      moves: v.moves ?? [],
    };
  } catch {
    return { ...EMPTY_JOURNAL };
  }
}
function writeJournal(j: Journal): void {
  // The tutorial never records anything, so a chapter cannot drift from one
  // visit to the next and cannot reach a real manager's leagues.
  if (isTutorial()) return;
  try {
    sessionStorage.setItem(JOURNAL_KEY, JSON.stringify(j));
  } catch {
    // Storage blocked: the league lives for this page load only.
  }
}
function buildWorld(seed: number, scenario: SimScenario): SimState[] {
  const world = createWorld(seed, { currentPeriod: SCENARIO_PERIOD[scenario] });
  const j = readJournal();
  for (const c of j.created)
    world.push(
      createLeagueFromWizard(c.answers, { id: c.id, ownerName: c.ownerName, worldSeed: seed }),
    );
  for (const jn of j.joined) {
    const league = world.find((x) => x.inviteCode === jn.code);
    if (league) joinSimLeague(league, jn.member);
  }
  for (const sv of j.settings) {
    const league = world.find((x) => x.leagueId === sv.leagueId);
    // UPGRADED ON THE WAY OUT, as every stored version is: a tab that saved
    // rules before settings version 3 holds the draft's old `type`.
    const settings = StoredLeagueSettings.safeParse(sv.settings);
    if (league && settings.success) saveSimSettings(league, settings.data, sv);
  }
  // One timeline: every league moves to each entry's time before it is applied.
  const timeline: (DraftEntry | MoveEntry)[] = [...j.draft, ...j.moves].sort((a, b) => a.at - b.at);
  for (const d of timeline) {
    const league = world.find((x) => x.leagueId === d.leagueId);
    if (!league) continue;
    advanceLeague(league, d.at);
    switch (d.kind) {
      case 'start':
        simStartDraft(league, d.at, 'commissioner');
        break;
      case 'intent':
        simDraftIntent(league, d.intent, d.at);
        break;
      case 'chat':
        simPostChat(league, {
          author: d.author,
          body: d.body,
          color: d.color,
          at: d.at,
          replyTo: d.replyTo ?? null,
          ...(d.sticker === undefined ? {} : { sticker: d.sticker }),
        });
        break;
      case 'react':
        simReactChat(league, d.messageId, d.emoji, d.who);
        break;
      case 'claim':
        simPlaceClaim(league, d.input, d.at);
        break;
      case 'withdraw':
        simWithdrawClaim(league, d.claimId);
        break;
      case 'reorder':
        simReorderClaims(league, d.teamId, d.claimIds);
        break;
      case 'add':
        simAddPlayer(league, d, d.at);
        break;
      case 'drop':
        simDropPlayer(league, d, d.at);
        break;
      case 'undo':
        simUndoTransaction(league, d.transactionId, d.at);
        break;
      case 'trade':
        simTradeIntent(league, d.intent, d.at);
        break;
      case 'block':
        simSetTradeBlock(league, d.teamId, d.playerId, d.wants, d.at);
        break;
      case 'team':
        simEditTeam(league, d.teamId, d.change);
        break;
      case 'hat':
        simPickHat(league, d.teamId, d.hat, d.at);
        break;
    }
  }
  return world;
}

function presetTime(state: SimState, preset: JumpPreset): number {
  const H = 3600 * 1000;
  const sun = state.sundayUtcMidnight;
  switch (preset) {
    case 'thu-pre':
      return sun - 3 * 24 * H - 1 * H;
    case 'sun-pre':
      return sun + 16 * H + 55 * 60 * 1000;
    case 'sun-mid':
      return defaultNow(state);
    case 'sun-late':
      return sun + 22.5 * H;
    case 'sun-night':
      return sun + 25.5 * H;
    case 'final':
      return sun + 3 * 24 * H;
    case 'wed-waivers':
      // Wednesday 3:05 AM Central: the default league's waivers have just run.
      return sun + 3 * 24 * H + 8 * H + 5 * 60 * 1000;
  }
}

export function SimProvider({
  children,
  seed: defaultSeed = 2026,
}: {
  children: ReactNode;
  seed?: number;
}) {
  const [scenario, setScenarioState] = useState<SimScenario>(readScenario);
  const [tutorial, setTutorialState] = useState<boolean>(isTutorial);
  const [world, setWorld] = useState(() => buildWorld(readSeed(defaultSeed), scenario));
  const [version, setVersion] = useState(0);
  const first = world[0]!;
  const [now, setNow] = useState(() => readClock()?.now ?? defaultNow(first));
  const [playing, setPlaying] = useState(() => readClock()?.playing ?? true);
  const [speed, setSpeed] = useState<SimSpeed>(() => readClock()?.speed ?? 30);
  const speedRef = useRef(speed);
  speedRef.current = speed;

  // In every league I am my team's owner, and my name is the session's
  // (ACCOUNTS.md: the display name changes; the id and the seat do not).
  // In the tutorial there is no account, so the protagonist seat simply reads
  // as yours. That is a sandbox, not a claim that anyone is signed in.
  const { session } = useSession();
  const displayName = tutorial ? 'You' : (session?.displayName ?? null);
  const asYou = tutorial;
  useEffect(() => {
    if (!displayName) return;
    let changed = false;
    for (const s of world)
      for (const t of s.teams) {
        if (!t.isMine) continue;
        if (t.ownerName !== displayName) {
          t.ownerName = displayName;
          changed = true;
        }
        // On the tour the demo's owner is "You", so "Ken's Vikings" reads as yours.
        const named = asYou
          ? t.name.replace(/^Ken's /, 'Your ')
          : t.name.replace(/^Your /, "Ken's ");
        if (named !== t.name) {
          t.name = named;
          changed = true;
        }
      }
    if (changed) setVersion((v) => v + 1);
  }, [world, displayName, asYou]);

  // THE READER'S ZONE, on every state the practice world renders (B-181): the
  // account's, or this device's for a visitor. An account with none reads the
  // world in its own zone, named. Set in place like the name above, because
  // the simulator's states are the objects every view is computed from.
  const readerZone =
    session === null
      ? (() => {
          try {
            return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
          } catch {
            return undefined;
          }
        })()
      : (session.timeZone ?? undefined);
  useEffect(() => {
    let changed = false;
    for (const s of world) {
      if (s.readerZone !== readerZone) {
        s.readerZone = readerZone;
        changed = true;
      }
    }
    if (changed) setVersion((v) => v + 1);
  }, [world, readerZone]);

  const { pathname } = useLocation();
  const routeLeagueId = leagueIdFromPath(pathname);
  const activeLeagueId = world.some((s) => s.leagueId === routeLeagueId) ? routeLeagueId : null;
  const state = world.find((s) => s.leagueId === activeLeagueId) ?? first;

  // The clock survives a reload so exploring the week isn't reset by a refresh.
  useEffect(() => {
    try {
      sessionStorage.setItem(CLOCK_KEY, JSON.stringify({ now, playing, speed }));
    } catch {
      // Storage blocked: the clock still runs for this page load.
    }
  }, [now, playing, speed]);

  useEffect(() => {
    if (!playing) return;
    const id = setInterval(() => setNow((n) => n + 1000 * speedRef.current), 1000);
    return () => clearInterval(id);
  }, [playing]);

  // Every league in the world shares one Sunday, so the first league's clock presets serve all.
  const jump = useCallback((preset: JumpPreset) => setNow(presetTime(first, preset)), [first]);

  const reseed = useCallback(
    (s: number) => {
      writeJournal({ ...EMPTY_JOURNAL });
      writeSeed(s);
      const next = createWorld(s, { currentPeriod: SCENARIO_PERIOD[scenario] });
      setWorld(next);
      setNow(defaultNow(next[0]!));
      setVersion((v) => v + 1);
    },
    [scenario],
  );

  const setScenario = useCallback(
    (s: SimScenario) => {
      writeJournal({ ...EMPTY_JOURNAL });
      try {
        sessionStorage.setItem(SCENARIO_KEY, s);
      } catch {
        // Storage blocked: the scenario lasts for this page load.
      }
      setScenarioState(s);
      const next = createWorld(first.seed, { currentPeriod: SCENARIO_PERIOD[s] });
      setWorld(next);
      setNow(defaultNow(next[0]!));
      setVersion((v) => v + 1);
    },
    [first.seed],
  );

  /**
   * Enter or leave the tutorial. Rebuilding is what makes a chapter repeatable:
   * on the way in the journal stops being read, so the world is the pure seeded
   * one; on the way out it is read again and the manager's own leagues come
   * back untouched, because the tutorial never wrote to it.
   */
  const setTutorial = useCallback(
    (on: boolean) => {
      writeTutorialFlag(on);
      setTutorialState(on);
      const next = buildWorld(first.seed, on ? 'regular' : scenario);
      setWorld(next);
      setNow(defaultNow(next[0]!));
      setVersion((v) => v + 1);
    },
    [first.seed, scenario],
  );

  const swapIn = useCallback<SimContextValue['swapIn']>(
    (leagueId, teamId, slot, slotIndex, playerId) => {
      const league = world.find((s) => s.leagueId === leagueId);
      if (!league) return 'Unknown league';
      const team = league.teams.find((t) => t.id === teamId);
      if (!team) return 'Unknown team';
      const target = team.lineup.find((r) => r.slot === slot && r.slotIndex === slotIndex);
      if (!target) return 'Unknown slot';
      const lockedAt = (id: string | null) => {
        if (!id) return null;
        const p = league.players.get(id)!;
        const g = league.proGames.find((x) => x.home === p.proTeam || x.away === p.proTeam);
        return g ? g.startAt : null;
      };
      const tLock = lockedAt(target.playerId);
      if (tLock !== null && now >= tLock) return 'That player is locked. His game already started.';
      const slotDef = football.slots.find((s) => s.id === slot)!;
      if (playerId) {
        const p = league.players.get(playerId);
        if (!p) return 'Unknown player';
        if (slotDef.kind === 'starter' && !slotDef.eligible.includes(p.positions[0]))
          return `${p.shortName} can't play ${slotDef.name}.`;
        const pLock = lockedAt(playerId);
        if (pLock !== null && now >= pLock)
          return `${p.shortName} is locked. His game already started.`;
        const source = team.lineup.find((r) => r.playerId === playerId);
        if (source) source.playerId = target.playerId;
        target.playerId = playerId;
      } else {
        // Empty the slot: move the player to the first empty bench row.
        const bench = team.lineup.find((r) => r.slot === 'BN' && r.playerId === null);
        if (!bench) return 'No open bench spot.';
        bench.playerId = target.playerId;
        target.playerId = null;
      }
      setVersion((v) => v + 1);
      return null;
    },
    [world, now],
  );

  const swap = useCallback<SimContextValue['swap']>(
    (teamId, slot, slotIndex, playerId) =>
      swapIn(state.leagueId, teamId, slot, slotIndex, playerId),
    [swapIn, state.leagueId],
  );

  const createLeague = useCallback<SimContextValue['createLeague']>(
    (answers, ownerName) => {
      const base = slugify(answers.name);
      const taken = new Set(world.map((s) => s.leagueId));
      let id = base;
      for (let i = 2; taken.has(id); i++) id = `${base}-${i}`;
      const league = createLeagueFromWizard(answers, { id, ownerName, worldSeed: first.seed });
      setWorld((w) => [...w, league]);
      const j = readJournal();
      writeJournal({ ...j, created: [...j.created, { id, answers, ownerName }] });
      setVersion((v) => v + 1);
      return id;
    },
    [world, first.seed],
  );

  const leagueByCode = useCallback<SimContextValue['leagueByCode']>(
    (code) => {
      const c = code.trim().toUpperCase();
      return world.find((s) => s.inviteCode === c) ?? null;
    },
    [world],
  );

  const joinLeague = useCallback<SimContextValue['joinLeague']>(
    (code, member) => {
      const league = leagueByCode(code);
      if (!league)
        return { ok: false, reason: 'No league with that code. Check with whoever sent it.' };
      const reason = joinSimLeague(league, member);
      if (reason) return { ok: false, reason };
      const j = readJournal();
      writeJournal({ ...j, joined: [...j.joined, { code: league.inviteCode, member }] });
      setVersion((v) => v + 1);
      return { ok: true, leagueId: league.leagueId };
    },
    [leagueByCode],
  );

  const saveSettings = useCallback<SimContextValue['saveSettings']>(
    (settings, opts) => {
      const result = saveSimSettings(state, settings, { ...opts, now });
      if (result.ok) {
        const j = readJournal();
        writeJournal({
          ...j,
          settings: [
            ...j.settings,
            {
              leagueId: state.leagueId,
              settings,
              reason: opts.reason,
              by: opts.by,
              unlock: opts.unlock ?? false,
              now,
            },
          ],
        });
        setVersion((v) => v + 1);
      }
      return result;
    },
    [state, now],
  );

  // The rooms move with the clock: AI seats and expired clocks pick as time passes.
  useEffect(() => {
    let changed = false;
    for (const s of world) if (advanceLeague(s, now)) changed = true;
    if (changed) setVersion((v) => v + 1);
  }, [now, world]);

  const journalDraft = useCallback((entry: Journal['draft'][number]) => {
    const j = readJournal();
    writeJournal({ ...j, draft: [...j.draft, entry] });
  }, []);

  const draftIntent = useCallback<SimContextValue['draftIntent']>(
    (intent) => {
      const events = simDraftIntent(state, intent, now);
      if (!events.some((e) => e.type === 'rejected'))
        journalDraft({ leagueId: state.leagueId, at: now, kind: 'intent', intent });
      setVersion((v) => v + 1);
      return events;
    },
    [state, now, journalDraft],
  );

  const draftStart = useCallback<SimContextValue['draftStart']>(() => {
    const events = simStartDraft(state, now, 'commissioner');
    journalDraft({ leagueId: state.leagueId, at: now, kind: 'start' });
    setVersion((v) => v + 1);
    return events;
  }, [state, now, journalDraft]);

  const postChat = useCallback<SimContextValue['postChat']>(
    (author, body, color, replyTo = null, sticker) => {
      simPostChat(state, {
        author,
        body,
        color,
        at: now,
        replyTo,
        ...(sticker === undefined ? {} : { sticker }),
      });
      journalDraft({
        leagueId: state.leagueId,
        at: now,
        kind: 'chat',
        author,
        body,
        color,
        ...(replyTo === null ? {} : { replyTo }),
        ...(sticker === undefined ? {} : { sticker }),
      });
      setVersion((v) => v + 1);
    },
    [state, now, journalDraft],
  );

  const reactChat = useCallback<SimContextValue['reactChat']>(
    (messageId, emoji) => {
      const who = state.teams.find((t) => t.isMine)?.ownerName ?? 'You';
      if (!simReactChat(state, messageId, emoji, who)) return;
      journalDraft({ leagueId: state.leagueId, at: now, kind: 'react', messageId, emoji, who });
      setVersion((v) => v + 1);
    },
    [state, now, journalDraft],
  );

  const previewSettings = useCallback<SimContextValue['previewSettings']>(
    (settings) => previewSimSettings(state, settings, now),
    [state, now],
  );

  // Roster moves and claims (WAIVERS.md). Each is validated by the sim,
  // journaled on success, and bumps the version so every view recomputes.
  const journalMove = useCallback((entry: MoveEntry) => {
    const j = readJournal();
    writeJournal({ ...j, moves: [...j.moves, entry] });
  }, []);
  const myTeamId = state.teams.find((t) => t.isMine)?.id ?? '';

  const placeClaim = useCallback<SimContextValue['placeClaim']>(
    (input) => {
      const full: ClaimInput = { ...input, teamId: myTeamId };
      const r = simPlaceClaim(state, full, now);
      if (!r.ok) return r;
      journalMove({ leagueId: state.leagueId, at: now, kind: 'claim', input: full });
      setVersion((v) => v + 1);
      return { ok: true, claimId: r.claim.id };
    },
    [state, now, myTeamId, journalMove],
  );

  const withdrawClaim = useCallback<SimContextValue['withdrawClaim']>(
    (claimId) => {
      const err = simWithdrawClaim(state, claimId);
      if (err) return err;
      journalMove({ leagueId: state.leagueId, at: now, kind: 'withdraw', claimId });
      setVersion((v) => v + 1);
      return null;
    },
    [state, now, journalMove],
  );

  const reorderClaims = useCallback<SimContextValue['reorderClaims']>(
    (claimIds) => {
      const err = simReorderClaims(state, myTeamId, claimIds);
      if (err) return err;
      journalMove({
        leagueId: state.leagueId,
        at: now,
        kind: 'reorder',
        teamId: myTeamId,
        claimIds,
      });
      setVersion((v) => v + 1);
      return null;
    },
    [state, now, myTeamId, journalMove],
  );

  const addPlayer = useCallback<SimContextValue['addPlayer']>(
    (playerId, dropPlayerIds) => {
      const r = simAddPlayer(state, { teamId: myTeamId, playerId, dropPlayerIds }, now);
      if (!r.ok) return r;
      journalMove({
        leagueId: state.leagueId,
        at: now,
        kind: 'add',
        teamId: myTeamId,
        playerId,
        dropPlayerIds,
      });
      setVersion((v) => v + 1);
      return r;
    },
    [state, now, myTeamId, journalMove],
  );

  const dropPlayer = useCallback<SimContextValue['dropPlayer']>(
    (playerId) => {
      const r = simDropPlayer(state, { teamId: myTeamId, playerId }, now);
      if (!r.ok) return r;
      journalMove({ leagueId: state.leagueId, at: now, kind: 'drop', teamId: myTeamId, playerId });
      setVersion((v) => v + 1);
      return r;
    },
    [state, now, myTeamId, journalMove],
  );

  const tradeIntent = useCallback<SimContextValue['tradeIntent']>(
    (intent) => {
      const events = simTradeIntent(state, intent, now);
      if (!events.some((e) => e.type === 'rejected'))
        journalMove({ leagueId: state.leagueId, at: now, kind: 'trade', intent });
      setVersion((v) => v + 1);
      return events;
    },
    [state, now, journalMove],
  );

  const setTradeBlock = useCallback<SimContextValue['setTradeBlock']>(
    (playerId, wants) => {
      const err = simSetTradeBlock(state, myTeamId, playerId, wants, now);
      if (err) return err;
      journalMove({
        leagueId: state.leagueId,
        at: now,
        kind: 'block',
        teamId: myTeamId,
        playerId,
        wants,
      });
      setVersion((v) => v + 1);
      return null;
    },
    [state, now, myTeamId, journalMove],
  );

  // A NAME AND COLORS ARE JOURNALED LIKE A MOVE, so a reload keeps them — and
  // the tutorial, which journals nothing, forgets them with everything else.
  const editTeam = useCallback<SimContextValue['editTeam']>(
    (change) => {
      const err = simEditTeam(state, myTeamId, change);
      if (err) return err;
      journalMove({ leagueId: state.leagueId, at: now, kind: 'team', teamId: myTeamId, change });
      setVersion((v) => v + 1);
      return null;
    },
    [state, now, myTeamId, journalMove],
  );

  const pickHat = useCallback<SimContextValue['pickHat']>(
    (hat) => {
      if (!simPickHat(state, myTeamId, hat, now)) return false;
      journalMove({ leagueId: state.leagueId, at: now, kind: 'hat', teamId: myTeamId, hat });
      setVersion((v) => v + 1);
      return true;
    },
    [state, now, myTeamId, journalMove],
  );

  const undoTransaction = useCallback<SimContextValue['undoTransaction']>(
    (transactionId) => {
      const err = simUndoTransaction(state, transactionId, now);
      if (err) return err;
      journalMove({ leagueId: state.leagueId, at: now, kind: 'undo', transactionId });
      setVersion((v) => v + 1);
      return null;
    },
    [state, now, journalMove],
  );

  const value = useMemo<SimContextValue>(
    () => ({
      world,
      activeLeagueId,
      state,
      version,
      now,
      playing,
      speed,
      setPlaying,
      setSpeed,
      setNow,
      jump,
      reseed,
      scenario,
      setScenario,
      tutorial,
      setTutorial,
      swap,
      swapIn,
      createLeague,
      leagueByCode,
      joinLeague,
      saveSettings,
      previewSettings,
      draftIntent,
      draftStart,
      postChat,
      reactChat,
      placeClaim,
      withdrawClaim,
      reorderClaims,
      addPlayer,
      dropPlayer,
      undoTransaction,
      tradeIntent,
      setTradeBlock,
      editTeam,
      pickHat,
    }),
    [
      world,
      activeLeagueId,
      state,
      version,
      now,
      playing,
      speed,
      jump,
      reseed,
      scenario,
      setScenario,
      tutorial,
      setTutorial,
      swap,
      swapIn,
      createLeague,
      leagueByCode,
      joinLeague,
      saveSettings,
      previewSettings,
      draftIntent,
      draftStart,
      postChat,
      reactChat,
      placeClaim,
      withdrawClaim,
      reorderClaims,
      addPlayer,
      dropPlayer,
      undoTransaction,
      tradeIntent,
      setTradeBlock,
      editTeam,
      pickHat,
    ],
  );

  return <SimContext.Provider value={value}>{children}</SimContext.Provider>;
}

/**
 * Whether a league id is one of the tutorial's own while one of its chapters
 * is open (B-527): its leagues are the simulator's then, whatever the
 * reader's hub holds. A local database seeded from the simulator's world has
 * leagues under the same ids, and an account in them read the tutorial's
 * chapters from those instead. Only while a chapter is open: the tutorial
 * mode outlives a visit to the tour, and a real league of the same id is a
 * real league outside it. Safe outside the providers, where it is false.
 */
export function useTutorialLeague(leagueId: string | null): boolean {
  const ctx = useContext(SimContext);
  const chapter = useChapterOpen();
  return (
    chapter &&
    ctx !== null &&
    ctx.tutorial &&
    leagueId !== null &&
    ctx.world.some((s) => s.leagueId === leagueId)
  );
}

export function useSim(): SimContextValue {
  const ctx = useContext(SimContext);
  if (!ctx) throw new Error('useSim must be used inside SimProvider');
  return ctx;
}
